import { useRef, useState, useCallback, useEffect } from "react";
import { Document, Page } from "react-pdf";
import { Loader2, X } from "lucide-react";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export const PdfViewer = ({
  file,
  pageNumber,
  onLoadSuccess,
  renderWidth,
  activeSignature,
  placements,
  onAdd,
  onUpdate,
  onRemove,
}) => {
  const wrapRef = useRef(null);
  const [rect, setRect] = useState({ width: 0, height: 0 });
  const dragInfo = useRef(null);

  const measure = useCallback(() => {
    const canvas = wrapRef.current?.querySelector(".react-pdf__Page__canvas");
    if (canvas) setRect({ width: canvas.clientWidth, height: canvas.clientHeight });
  }, []);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const canvasRect = () =>
    wrapRef.current.querySelector(".react-pdf__Page__canvas").getBoundingClientRect();

  const handlePageClick = (e) => {
    if (!activeSignature) return;
    if (e.target.closest("[data-sig-box]")) return; // don't add when interacting with a box
    const box = canvasRect();
    const relW = 0.28;
    const aspect = activeSignature.height / activeSignature.width;
    const boxWpx = relW * box.width;
    const boxHpx = boxWpx * aspect;
    const relX = clamp((e.clientX - box.left - boxWpx / 2) / box.width, 0, 1 - relW);
    const relY = clamp((e.clientY - box.top - boxHpx / 2) / box.height, 0, 1 - boxHpx / box.height);
    onAdd({
      page: pageNumber,
      relX,
      relY,
      relW,
      dataUrl: activeSignature.dataUrl,
      imgW: activeSignature.width,
      imgH: activeSignature.height,
    });
  };

  const onBoxPointerDown = (e, p) => {
    e.stopPropagation();
    e.preventDefault();
    const box = canvasRect();
    dragInfo.current = {
      id: p.id,
      startX: e.clientX,
      startY: e.clientY,
      origRelX: p.relX,
      origRelY: p.relY,
      relW: p.relW,
      aspect: p.imgH / p.imgW,
      boxW: box.width,
      boxH: box.height,
    };
    window.addEventListener("pointermove", onBoxPointerMove);
    window.addEventListener("pointerup", onBoxPointerUp);
  };
  const onBoxPointerMove = (e) => {
    const d = dragInfo.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.boxW;
    const dy = (e.clientY - d.startY) / d.boxH;
    const relH = (d.relW * d.boxW * d.aspect) / d.boxH;
    onUpdate(d.id, {
      relX: clamp(d.origRelX + dx, 0, 1 - d.relW),
      relY: clamp(d.origRelY + dy, 0, 1 - relH),
    });
  };
  const onBoxPointerUp = () => {
    dragInfo.current = null;
    window.removeEventListener("pointermove", onBoxPointerMove);
    window.removeEventListener("pointerup", onBoxPointerUp);
  };

  const pagePlacements = placements.filter((p) => p.page === pageNumber);

  return (
    <div className="flex justify-center">
      <Document
        file={file}
        onLoadSuccess={(pdf) => onLoadSuccess(pdf.numPages)}
        loading={
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Chargement du document…
          </div>
        }
        error={<div className="p-8 text-center text-sm text-destructive">Impossible d'afficher ce PDF.</div>}
      >
        <div
          ref={wrapRef}
          className="relative inline-block"
          onClick={handlePageClick}
          style={{ cursor: activeSignature ? "copy" : "default" }}
          data-testid="pdf-page-wrap"
        >
          <Page
            pageNumber={pageNumber}
            width={renderWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            onRenderSuccess={measure}
          />
          {rect.width > 0 &&
            pagePlacements.map((p) => {
              const aspect = p.imgH / p.imgW;
              const style = {
                left: p.relX * rect.width,
                top: p.relY * rect.height,
                width: p.relW * rect.width,
                height: p.relW * rect.width * aspect,
              };
              return (
                <div
                  key={p.id}
                  data-sig-box
                  data-testid="signature-box"
                  onPointerDown={(e) => onBoxPointerDown(e, p)}
                  className="sig-box group absolute z-10 rounded-sm border-2 border-dashed border-primary bg-primary/5"
                  style={style}
                >
                  <img
                    src={p.dataUrl}
                    alt="signature"
                    draggable={false}
                    className="pointer-events-none h-full w-full select-none object-contain"
                  />
                  <button
                    type="button"
                    data-testid="remove-signature-box"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(p.id);
                    }}
                    className="absolute -right-2.5 -top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white opacity-80 shadow transition-opacity hover:opacity-100"
                    title="Retirer cette signature"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
        </div>
      </Document>
    </div>
  );
};
